import {
  HorizontalNavigationTab,
  HorizontalNavigationTabOption,
} from "@risalabs_frontend_org/oasis-ui-kit";
import { useState } from "react";
import type { CriteriaScreeningResponse } from "../../../data-model/eligibilityDetail";
import CriteriaEvaluationModal from "../../../components/modals/criteria-evaluation-modal";
import { useCriteriaEvaluationAnalysis } from "../../../hooks/useCriteriaEvaluationAnalysis";
import { normalizeCriteriaEvaluationStatus } from "../../../utils/criteriaEvaluationModal";
import { useEligibilityDetailContext } from "../EligibilityDetailContext";
import { useSearchParams } from "react-router-dom";
import { useEffect } from "react";
import ActionPendingTab from "./actionPending/ActionPendingTab";
import CriteriaEvaluationError from "./criteriaEvaluation/CriteriaEvaluationError";
import CriteriaEvaluationTab from "./criteriaEvaluation/CriteriaEvaluationTab";
import CriteriaEvaluationLoading from "./criteriaEvaluation/CriteriaEvaluationLoading";
import PrimaryEvaluationTab from "./primaryEvaluation/PrimaryEvaluationTab";

/** Tab values for the single-patient eligibility details navigation strip. */
type EligibilityDetailsTab =
  | "action-pending"
  | "primary-evaluation"
  | "criteria-evaluation";

const ELIGIBILITY_DETAILS_TABS: (HorizontalNavigationTabOption & {
  value: EligibilityDetailsTab;
})[] = [
    {
      label: "Action Pending",
      value: "action-pending",
    },
    {
      label: "Primary Evaluation",
      value: "primary-evaluation",
    },
    {
      label: "Criteria Evaluation",
      value: "criteria-evaluation",
    },
  ];

const DEFAULT_TAB: EligibilityDetailsTab = ELIGIBILITY_DETAILS_TABS[0].value;

const tabFromSearchParams = (
  searchParams: URLSearchParams
): EligibilityDetailsTab => {
  if (searchParams.get("tab") === "criteria") return "criteria-evaluation";
  if (searchParams.get("tab") === "primary") return "primary-evaluation";
  return DEFAULT_TAB;
};

/**
 * Navigable details area under the summary cards on the single-patient
 * eligibility details screen. Takes up the remaining vertical space.
 *
 * @returns Navigation container with the selected tab's content
 */
const EligibilityTabs = () => {
  const { header, trialId, mrn } = useEligibilityDetailContext();
  const criteriaEvaluation = useCriteriaEvaluationAnalysis(trialId, mrn);
  const [isCriteriaEvaluationModalOpen, setIsCriteriaEvaluationModalOpen] =
    useState(false);
  const [searchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab");
  const [selectedTab, setSelectedTab] = useState<EligibilityDetailsTab>(() => {
    return tabFromSearchParams(searchParams);
  });
  useEffect(() => {
    if (requestedTab === "criteria") setSelectedTab("criteria-evaluation");
    else if (requestedTab === "primary") setSelectedTab("primary-evaluation");
  }, [requestedTab]);

  /**
   * Commits the tab clicked in the navigation strip, ignoring values that are
   * not part of the configured tab list.
   *
   * @param value - Value emitted by the navigation tab component
   */
  const handleTabChange = (value: string) => {
    const nextTab = ELIGIBILITY_DETAILS_TABS.find(
      (tab) => tab.value === value
    )?.value;

    if (nextTab) {
      setSelectedTab(nextTab);
      if (nextTab === "criteria-evaluation") {
        criteriaEvaluation.clearError();
        setIsCriteriaEvaluationModalOpen(true);
      }
    }
  };

  return (
    <div className="single-patient-eligibility-details__left-content--nav-container flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="navigation--tabs mb-5 flex items-center border-b border-primaryGray-14 bg-white">
        <div className="min-w-0 flex-1">
          <HorizontalNavigationTab
            onChange={handleTabChange}
            tabs={ELIGIBILITY_DETAILS_TABS}
            value={selectedTab}
            backgroundColor="white"
            rounded
          />
        </div>
        {selectedTab === "criteria-evaluation" &&
          criteriaEvaluation.criteriaDetail !== null &&
          !criteriaEvaluation.isInitiating &&
          !criteriaEvaluation.error && (
            <button
              type="button"
              className="ml-4 mr-3 shrink-0 rounded border border-primaryGray-14 bg-white px-3 py-2 text-xs font-semibold text-primaryGray-2 transition-colors hover:bg-primaryGray-16"
              onClick={() =>
                void criteriaEvaluation.initiate().catch(() => {})
              }
            >
              Re-Initiate Evaluation
            </button>
          )}
      </div>
      <div className="navigation--tab-content flex min-h-0 flex-1 flex-col overflow-hidden">
        <EligibilityTabContent
          selectedTab={selectedTab}
          criteriaEvaluationLoading={criteriaEvaluation.isInitiating}
          criteriaEvaluationError={criteriaEvaluation.error}
          criteriaDetail={criteriaEvaluation.criteriaDetail}
          onRetryCriteriaEvaluation={() =>
            void criteriaEvaluation.initiate().catch(() => {})
          }
        />
      </div>
      <CriteriaEvaluationModal
        isOpen={isCriteriaEvaluationModalOpen}
        status={normalizeCriteriaEvaluationStatus(header.eligibilityStatus)}
        onInitiate={criteriaEvaluation.initiate}
        onClose={() => setIsCriteriaEvaluationModalOpen(false)}
      />
    </div>
  );
};

/**
 * Renders the panel body for the currently selected tab.
 *
 * @param props - Component props
 * @param props.selectedTab - Value of the active navigation tab
 * @returns Content for the active tab
 */
const EligibilityTabContent = ({
  selectedTab,
  criteriaEvaluationLoading,
  criteriaEvaluationError,
  criteriaDetail,
  onRetryCriteriaEvaluation,
}: {
  selectedTab: EligibilityDetailsTab;
  criteriaEvaluationLoading: boolean;
  criteriaEvaluationError: string | null;
  criteriaDetail: CriteriaScreeningResponse | null;
  onRetryCriteriaEvaluation: () => void;
}) => {
  switch (selectedTab) {
    case "primary-evaluation":
      return (
        <div className="h-full min-h-0">
          <PrimaryEvaluationTab />
        </div>
      );
    case "criteria-evaluation":
      if (criteriaEvaluationLoading) {
        return <CriteriaEvaluationLoading />;
      }
      if (criteriaEvaluationError) {
        return (
          <CriteriaEvaluationError
            error={criteriaEvaluationError}
            onRetry={onRetryCriteriaEvaluation}
          />
        );
      }
      return (
        <div className="h-full min-h-0">
          <CriteriaEvaluationTab criteriaDetail={criteriaDetail} />
        </div>
      );
    case "action-pending":
    default:
      return (
        <div className="h-full min-h-0">
          <ActionPendingTab />
        </div>
      );
  }
};

export default EligibilityTabs;
